import { action, observable, runInAction } from 'mobx';
import { ErrorInterface } from 'modules/core/api/globalApi';
import controller from '../controller';

/** Привязка «корп. клиент ↔ исполнитель» (GET /v1/corporate_executor?executorPartnerId=):
 *  в списке по исполнителю — какие корп. клиенты к нему привязаны. */
export interface CorporateExecutorItemInterface {
  id: string,
  corporatePartnerId?: string,
  corporatePartnerName?: string,
  executorPartnerId?: string,
  executorPartnerName?: string,
  serviceId?: string | number,
  serviceName?: string,
  deletedDate?: string | null,
  [key: string]: unknown,
}

/** Партнёр типа «корпоративный клиент» (GET /v1/partner/corporate_clients). */
export interface CorporateClientItemInterface {
  id: string,
  name?: string,
  isCorporateClient?: boolean,
  [key: string]: unknown,
}

/** Сервис, доступный и корп. клиенту, и исполнителю (GET /v1/...available_to_partner). */
export interface ServiceAvailableToPartnersItemInterface {
  id: string | number,
  name?: string,
  [key: string]: unknown,
}

/** Пользователь корп. клиента из привязки (GET /v1/corporate_executor_user?corporateExecutorId=). */
export interface CorporateExecutorUserItemInterface {
  userId: string,
  userName?: string,
  [key: string]: unknown,
}

export interface CorporateExecutorStore {
  // привязки по исполнителю executorPartnerId (бывш. corporateExecutorsList)
  executorsList: CorporateExecutorItemInterface[],
  executorPartnerId: string | null,
  loadingExecutorsList: boolean,

  // корп. клиенты (бывш. corporateClients) — вечный кэш на сессию: список меняется
  // редко; в Redux качался заново на каждый mount селектора/фильтра
  corporateClients: CorporateClientItemInterface[],
  corporateClientsLoaded: boolean,
  loadingCorporateClients: boolean,

  // сервисы, доступные корп. клиенту и исполнителю (бывш. servicesAvailableToPartners)
  servicesAvailableToPartners: ServiceAvailableToPartnersItemInterface[],
  loadingServicesAvailableToPartners: boolean,

  // пользователи привязки corporateExecutorId (бывш. corporateExecutorsUserList)
  executorUsers: CorporateExecutorUserItemInterface[],
  executorUsersByCorporateExecutorId: string | null,
  loadingExecutorUsers: boolean,

  error: ErrorInterface,
}

const initialValues: CorporateExecutorStore = {
  executorsList: [],
  executorPartnerId: null,
  loadingExecutorsList: false,

  corporateClients: [],
  corporateClientsLoaded: false,
  loadingCorporateClients: false,

  servicesAvailableToPartners: [],
  loadingServicesAvailableToPartners: false,

  executorUsers: [],
  executorUsersByCorporateExecutorId: null,
  loadingExecutorUsers: false,

  error: null,
};

export const corporateExecutorStore = observable(initialValues);

// запрос в полёте (не обзервабл) — single-flight на дублирующие mount'ы селекторов
let corporateClientsInFlight = false;

export const corporateExecutorActions = {

  handleError: action((response) => {
    if (response.errorData) {
      const { errorData } = response;
      corporateExecutorStore.error = {
        message: errorData?.message,
        errorCode: errorData?.errorCode,
        timestamp: errorData?.timestamp,
        status: response?.status,
        connectError: response?.connectError,
        isError: response?.error,
      };
      throw errorData;
    }
  }),

  clearError: action(() => {
    corporateExecutorStore.error = null;
  }),

  // GET /v1/corporate_executor?executorPartnerId= — привязки «корп. клиент ↔ исполнитель»
  // по исполнителю (страница «Корпоративные клиенты» на карточке партнёра). Слот без кэша:
  // страница перечитывает список после CRUD привязок.
  async getCorporateExecutors(data: { executorPartnerId: string }) {
    runInAction(() => {
      corporateExecutorStore.executorPartnerId = data.executorPartnerId;
      corporateExecutorStore.loadingExecutorsList = true;
    });
    try {
      const response = await controller.getPartnerCorporateExecutor(data);

      corporateExecutorActions.handleError(response);
      runInAction(() => {
        corporateExecutorStore.executorsList = response?.data ?? [];
      });
    } catch (err) {
      this.handleError(err);
      runInAction(() => {
        corporateExecutorStore.executorsList = [];
        corporateExecutorStore.executorPartnerId = null;
      });
    } finally {
      runInAction(() => {
        corporateExecutorStore.loadingExecutorsList = false;
      });
    }
  },

  // GET /v1/partner/corporate_clients — все корп. клиенты. Кэш на сессию (в Redux качался
  // на каждый mount PartnerSelector/фильтров); force=true перечитывает после CRUD.
  async getCorporateClients(force = false) {
    if (!force && (corporateExecutorStore.corporateClientsLoaded || corporateClientsInFlight)) {
      return;
    }
    corporateClientsInFlight = true;
    runInAction(() => {
      corporateExecutorStore.loadingCorporateClients = true;
    });
    try {
      const response = await controller.getPartnerCorporateClients();

      corporateExecutorActions.handleError(response);
      runInAction(() => {
        corporateExecutorStore.corporateClientsLoaded = true;
        corporateExecutorStore.corporateClients = response?.data ?? [];
      });
    } catch (err) {
      this.handleError(err);
    } finally {
      corporateClientsInFlight = false;
      runInAction(() => {
        corporateExecutorStore.loadingCorporateClients = false;
      });
    }
  },

  // GET /v1/...available_to_partner — сервисы, доступные корп. клиенту и исполнителю
  // (модалка привязки корп. клиента). Слот без кэша: модалка редкая, состав зависит от пары id.
  async getServicesAvailableToPartners(data: { corporatePartnerId: string, executorPartnerId: string }) {
    runInAction(() => {
      corporateExecutorStore.loadingServicesAvailableToPartners = true;
    });
    try {
      const response = await controller.getPartnerServicesAvailableToPartners(data);

      corporateExecutorActions.handleError(response);
      runInAction(() => {
        corporateExecutorStore.servicesAvailableToPartners = response?.data ?? [];
      });
    } catch (err) {
      this.handleError(err);
      runInAction(() => {
        corporateExecutorStore.servicesAvailableToPartners = [];
      });
    } finally {
      runInAction(() => {
        corporateExecutorStore.loadingServicesAvailableToPartners = false;
      });
    }
  },

  // GET /v1/corporate_executor_user?corporateExecutorId= — пользователи привязки.
  // Слот по corporateExecutorId; clear при закрытии списка.
  async getExecutorUsers(corporateExecutorId: string) {
    runInAction(() => {
      corporateExecutorStore.executorUsersByCorporateExecutorId = corporateExecutorId;
      corporateExecutorStore.loadingExecutorUsers = true;
    });
    try {
      const response = await controller.getPartnerCorporateExecutorUser(corporateExecutorId);

      corporateExecutorActions.handleError(response);
      runInAction(() => {
        corporateExecutorStore.executorUsers = response?.data ?? [];
      });
    } catch (err) {
      this.handleError(err);
      runInAction(() => {
        // чинит бывш. Redux-баг: dispatchFail писал список в ключ с русской «с»
        // (сorporateExecutorsUserList) — при ошибке в сторе оставался старый список
        corporateExecutorStore.executorUsers = [];
        corporateExecutorStore.executorUsersByCorporateExecutorId = null;
      });
    } finally {
      runInAction(() => {
        corporateExecutorStore.loadingExecutorUsers = false;
      });
    }
  },

  clearExecutorUsers: action(() => {
    corporateExecutorStore.executorUsers = [];
    corporateExecutorStore.executorUsersByCorporateExecutorId = null;
    corporateExecutorStore.error = null;
  }),

  // CRUD привязок «корп. клиент ↔ исполнитель» и пользователей привязок — страницы
  // «Корпоративные клиенты» партнёра. Кэши не трогаем: страницы перечитывают списки
  // после CRUD. При ошибке — error в стор + исключение (модалки показывают toastr).
  async createCorporateExecutor(params: Record<string, unknown>): Promise<void> {
    const response = await controller.createPartnerCorporateExecutor(params);
    corporateExecutorActions.handleError(response);
  },

  async restoreCorporateExecutor(id: string): Promise<void> {
    const response = await controller.restorePartnerCorporateExecutor(id);
    corporateExecutorActions.handleError(response);
  },

  async deleteCorporateExecutor(id: string): Promise<void> {
    const response = await controller.deletePartnerCorporateExecutor(id);
    corporateExecutorActions.handleError(response);
  },

  async createCorporateExecutorUser(params: Record<string, unknown>): Promise<void> {
    const response = await controller.createPartnerCorporateExecutorUser(params);
    corporateExecutorActions.handleError(response);
  },

  async deleteCorporateExecutorUser(params: { corporateExecutorId: string, userId: string }): Promise<void> {
    const response = await controller.deletePartnerCorporateExecutorUser(params);
    corporateExecutorActions.handleError(response);
  },

};
